import { useState } from "react";
import { Link } from "react-router-dom";
import "../../assets/scss/sidebar.css";
import { useChatHistory } from "../../context/chatHistoryContext";
import { useAuth } from "../../context/authContext";
import { useChat } from "../../context/chatContext";
import Alert from "./alerts/alert";
import { ContactModal } from "./modal";
import ScheduleAdvisorSidebar from "./scheduleAdvisorSidebar";
import UploadFilesSidebar from "./uploadFilesSidebar";
import IngestedFilesSidebar from "./ingestedFilesSidebar";
import UploadLogoSidebar from "./uploadLogoSidebar";
import AddData from "../private/addData";

const Sidebar = () => {
  const [expanded, setExpanded] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showScheduleAdvisorSidebar, setShowScheduleAdvisorSidebar] = useState(false);
  const [showUploadFilesSidebar, setShowUploadFilesSidebar] = useState(false);
  const [showIngestedFilesSidebar, setShowIngestedFilesSidebar] = useState(false);
  const [showUploadLogoSidebar, setShowUploadLogoSidebar] = useState(false);
  const [showAddDataSidebar, setShowAddDataSidebar] = useState(false);
  const { isAuthenticated, userData } = useAuth();
  const { openChatHistory, closeChatHistory } = useChatHistory();
  const { resetChatState } = useChat();

  const toggleSidebar = () => {
    setExpanded(!expanded);
  };

  // Helper function to close all modals and sidebars
  const closeAllModalsAndSidebars = () => {
    setShowFeedbackModal(false);
    setShowScheduleAdvisorSidebar(false);
    setShowUploadFilesSidebar(false);
    setShowIngestedFilesSidebar(false);
    setShowUploadLogoSidebar(false);
    setShowAddDataSidebar(false);
    closeChatHistory();
  };

  // Helper function to close all modals and sidebars except Add Data
  const closeAllModalsAndSidebarsExceptAddData = () => {
    setShowFeedbackModal(false);
    setShowScheduleAdvisorSidebar(false);
    setShowUploadFilesSidebar(false);
    setShowIngestedFilesSidebar(false);
    setShowUploadLogoSidebar(false);
    closeChatHistory();
  };

  // Helper function for home navigation
  const handleHomeClick = (e: React.MouseEvent) => {
    closeAllModalsAndSidebars();
    resetChatState(); // Reset chat context including selected use case
  };

  const renderMenuItems = (items: any[]) => {
    return items.map((item, index) => (
      <li key={index} className="sidebar-item">
        {item.onClick ? (
          <Link to={item.path || '#'} className="sidebar-link" onClick={item.onClick} title={!expanded ? item.text : undefined}>
            <i className={`bi ${item.icon}`} style={{fontSize: '1.5rem'}}></i>
            {expanded && <span className="sidebar-text">{item.text}</span>}
          </Link>
        ) : (
          <Link to={item.path} className="sidebar-link" title={!expanded ? item.text : undefined}>
            <i className={`bi ${item.icon}`} style={{fontSize: '1.5rem'}}></i>
            {expanded && <span className="sidebar-text">{item.text}</span>}
          </Link>
        )}
      </li>
    ));
  };

  const handleAllConversationClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    closeAllModalsAndSidebars();
    if (isAuthenticated) {
      openChatHistory();
    } else {
      setAlertVisible(true);
    }
  };

  const handleFeedbackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllModalsAndSidebars();
    setShowFeedbackModal(true);
  };

  const handleScheduleAdvisorClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllModalsAndSidebars();
    setShowScheduleAdvisorSidebar(true);
  };

  const handleUploadFilesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllModalsAndSidebars();
    setShowUploadFilesSidebar(true);
  };

  const handleIngestFilesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllModalsAndSidebars();
    setShowIngestedFilesSidebar(true);
  };

  const handleSwitchToUpload = () => {
    closeAllModalsAndSidebarsExceptAddData();
    setShowUploadFilesSidebar(true);
  };

  const handleSwitchToIngestion = () => {
    closeAllModalsAndSidebarsExceptAddData();
    setShowIngestedFilesSidebar(true);
  };

  const handleUploadLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllModalsAndSidebars();
    setShowUploadLogoSidebar(true);
  };

  const handleAddDataClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllModalsAndSidebarsExceptAddData();
    setShowAddDataSidebar(true);
  };

  const topMenuItems = [
    { icon: "bi-house-door", text: "Home", path: "/", onClick: handleHomeClick },
    { icon: "bi-chat-left-text", text: "Chat History", path: "/chat", onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => handleAllConversationClick(e)},
  ];
  
  // Admin-only menu items - only show if user is admin
  const beforemiddleMenuItems = userData?.is_admin ? [
      { icon: "bi-cloud-upload", text: "Upload Files", path: "#", onClick: handleUploadFilesClick },
      { icon: "bi-robot", text: "Ingest Files", path: "#", onClick: handleIngestFilesClick },
  ] : [];
  
  const middleMenuItems = [
      // Only show "Add Data" for admins and advisors (account_type = 2)
      ...(userData?.is_admin || userData?.account_type === 2 ? [{ icon: "bi-file-earmark-plus", text: "Add Data", path: "/documents", onClick: handleAddDataClick }] : []),
      // Only show "View Data" if user is admin
      ...(userData?.is_admin ? [{ icon: "bi-file-earmark-text", text: "View Data", path: "/viewFormData", onClick: (e: React.MouseEvent) => { closeAllModalsAndSidebars(); } }] : [])
  ];
  
  // Admin-only menu items - only show if user is admin
  const afterMenuItems =  [
    ...(userData?.account_type === 3 ? [{ icon: "bi-person-gear", text: "Manage Customers", path: "/manageCustomers", onClick: (e: React.MouseEvent) => { closeAllModalsAndSidebars(); } }] : []),
    ...(userData?.is_admin || userData?.account_type === 4 ? [{ icon: "bi-person-gear", text: "User Management", path: "/adminfeatures/management/users", onClick: (e: React.MouseEvent) => { closeAllModalsAndSidebars(); } }] : []),
    ...(userData?.is_admin || userData?.account_type === 4 ? [{ icon: "bi-activity", text: "Activity Logs", path: "/adminFeatures/reporting/users", onClick: (e: React.MouseEvent) => { closeAllModalsAndSidebars(); } }] : []),
    ...(userData?.is_admin ? [{ icon: "bi-images", text: "Upload Logo", path: "#", onClick: handleUploadLogoClick }] : []),
   
  ];
  
  const bottomMenuItems = [
    { icon: 'bi-calendar2-check', text: "Schedule Advisor Session", path: "/schedule-advisor-discussion", onClick: handleScheduleAdvisorClick },
    { icon: "bi-clipboard2-check", text: "Feedback", path: "/feedback", onClick: handleFeedbackClick },
    // { icon: "bi-gear", text: "Settings", path: "/settings", onClick: (e: React.MouseEvent) => { closeAllModalsAndSidebars(); } },
    // { icon: "bi-question-circle", text: "Help", path: "/help", onClick: (e: React.MouseEvent) => { closeAllModalsAndSidebars(); } },
  ];

  return (
    <>
      <div className={`sidebar ${expanded ? "expanded" : "collapsed"}`}>
        <div className="sidebar-header">
          {expanded ? (
            <button onClick={toggleSidebar} className="close-btn">
              <i className="bi bi-x-lg"></i>
            </button>
          ) : (
            <button onClick={toggleSidebar} className="hamburger-btn" title="Expand menu">
              <i className="bi bi-list"></i>
            </button>
          )}
        </div>
        <div className="sidebar-content">
          {/* Top menu items */}
          <ul className="sidebar-menu top-menu">
            {renderMenuItems(topMenuItems)}
          </ul>
          
          {/* Middle menu items - only render if they have content */}
          {beforemiddleMenuItems.length > 0 && (
            <ul className="sidebar-menu middle-menu">
              {renderMenuItems(beforemiddleMenuItems)}
              
            </ul>
          )}
          {middleMenuItems.length > 0 && (
            <ul className="sidebar-menu middle-menu">
              {renderMenuItems(middleMenuItems)}
            </ul>
          )}
          {afterMenuItems.length > 0 && (
            <ul className="sidebar-menu middle-menu">
              {renderMenuItems(afterMenuItems)}
            </ul>
          )}
          
          {/* Bottom menu items */}
          <ul className="sidebar-menu bottom-menu">
            {renderMenuItems(bottomMenuItems)}
          </ul>
        </div>
        <Alert
          message="Loads only when logged in."
          type="warning"
          show={alertVisible}
          onClose={() => setAlertVisible(false)}
        />
      </div>
      
      {/* Contact Modal - moved outside sidebar container */}
      <ContactModal 
        show={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)} 
        type="feedback" 
      />

      {/* Schedule Advisor Sidebar */}
      <ScheduleAdvisorSidebar 
        isOpen={showScheduleAdvisorSidebar}
        onClose={() => setShowScheduleAdvisorSidebar(false)}
      />

      {/* Upload Files Sidebar */}
      <UploadFilesSidebar 
        isOpen={showUploadFilesSidebar}
        onClose={() => setShowUploadFilesSidebar(false)}
        onSwitchToIngestion={handleSwitchToIngestion}
      />

      {/* Ingested Files Sidebar */}
      <IngestedFilesSidebar 
        isOpen={showIngestedFilesSidebar}
        onClose={() => setShowIngestedFilesSidebar(false)}
        onSwitchToUpload={handleSwitchToUpload}
      />

      {/* Upload Logo Sidebar */}
      <UploadLogoSidebar 
        isOpen={showUploadLogoSidebar}
        onClose={() => setShowUploadLogoSidebar(false)}
      />

      {/* Add Data Sidebar */}
      <AddData 
        isSidebarOpen={showAddDataSidebar}
        setIsSidebarOpen={setShowAddDataSidebar}
      />
    </>
  );
};

export default Sidebar;